import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  TextField, Button, Paper, Typography, CircularProgress, 
  IconButton, InputAdornment, Checkbox, FormControlLabel 
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import ReactModal from 'react-modal';
import axios from 'axios';
import CryptoJS from 'crypto-js';
import { useTranslation } from 'react-i18next';
import styles from './Register.module.scss';
import { IinContext } from '../../context/IinContext';
import { PasswordForm } from '../../components';
import { CODE_LENGTH, getCodeErrorMessage, isCodeComplete, sanitizeCode } from '../../utils/verificationCode';
import Cookies from 'js-cookie';

ReactModal.setAppElement('#root');

export const Register = ({ isSmsContext, iinAndBin }) => {
  const { t } = useTranslation();
  const { iin, iinOrBin, setIin, physPersonFullname, setPhysPersonFullname, iinForBin, orgName, firstName, lastName, bin } = useContext(IinContext);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [resendTimer, setResendTimer] = useState(120);
  const [isResendDisabled, setIsResendDisabled] = useState(true);

  const [iinError, setIinError] = useState(false);
  const [iinErrorMessage, setIinErrorMessage] = useState('');
  const [emailError, setEmailError] = useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState(false);
  const [confirmPasswordErrorMessage, setConfirmPasswordErrorMessage] = useState('');

  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);

  const [surname, setSurname] = useState('');
  const [name, setName] = useState('');
  const [patronymic, setPatronymic] = useState('');
  const [isAgreed, setIsAgreed] = useState(false);
  const [agreementError, setAgreementError] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [surnameError, setSurnameError] = useState(false);
  const [surnameErrorMessage, setSurnameErrorMessage] = useState('');
  const [nameError, setNameError] = useState(false);
  const [nameErrorMessage, setNameErrorMessage] = useState('');

  const navigate = useNavigate();

  const handleOpenModal = useCallback((e) => {
    e?.preventDefault();
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  useEffect(() => {
    let interval = null;
    if (resendTimer > 0) {
      setIsResendDisabled(true);
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    } else {
      setIsResendDisabled(false);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  useEffect(() => {
    if (isSmsContext) {
      const fullName = `${surname} ${name} ${patronymic}`.trim();
      setPhysPersonFullname(fullName);
    } else {
      setPhysPersonFullname(physPersonFullname);
    }
  }, [isSmsContext, surname, name, patronymic, setPhysPersonFullname, physPersonFullname]);

  useEffect(() => {
    if (iinAndBin) {
      setIin(iinAndBin);
    }
  }, [iinAndBin, setIin]);

  const handleRegisterKeyDown = (e) => {
    if (e.key === 'Enter' && email && password && confirmPassword && isAgreed) {
      handleSubmit(e);
    }
  };

  const handleCodeKeyDown = (e) => {
    if (e.key === 'Enter' && verificationCode) {
      handleCodeSubmit(e);
    }
  };

  const handleEmailKeyDown = (e) => {
    if (e.key === 'Enter' && email) {
      handleEmailSubmit();
    }
  };

  const validateIin = (iin) => {
    const iinRegex = /^\d{12}$/;
    if (!iinRegex.test(iin)) {
      return t('register.iinError');
    }
    return '';
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return t('register.emailError');
    }
    return '';
  };

  const validatePassword = (password) => {
    const minLength = 8;
    const hasLatin = /^[a-zA-Z0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]+$/.test(password);
    const hasNumber = /\d/.test(password);

    if (password.length < minLength) {
      return t('register.passwordLengthError');
    }
    if (!hasLatin) {
      return t('register.passwordLatinError');
    }
    if (!hasNumber) {
      return t('register.passwordNumberError');
    }
    return '';
  };

  const sendResetCode = async (iin) => {
    try {
      const response = await axios.post('/kcssobackext/sso/external/sendResetCode', {
        iinOrBin: !iin ? iinForBin : iin,
        method: "email"
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      setCodeSent(true);
      setResendTimer(120);
    } catch (error) {
      console.error('Failed to send reset code:', error);
    }
  };

  const validateName = (value, fieldName) => {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return t(`register.enter${fieldName}`);
    }
    if (trimmedValue.length <= 1) {
      return t(`register.enter${fieldName}`);
    }
    return '';
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    const surnameErrorMsg = isSmsContext ? validateName(surname, 'Lastname') : '';
    const nameErrorMsg = isSmsContext ? validateName(name, 'Name') : '';
    const emailErrorMsg = validateEmail(email);
    const passwordErrorMsg = validatePassword(password);
    const confirmPasswordErrorMsg = password !== confirmPassword ? t('register.passwordMismatch') : '';

    if (surnameErrorMsg || nameErrorMsg || emailErrorMsg || passwordErrorMsg || confirmPasswordErrorMsg || !isAgreed) {
      setSurnameError(Boolean(surnameErrorMsg));
      setSurnameErrorMessage(surnameErrorMsg);
      setNameError(Boolean(nameErrorMsg));
      setNameErrorMessage(nameErrorMsg);
      setEmailError(Boolean(emailErrorMsg));
      setEmailErrorMessage(emailErrorMsg);
      setPasswordError(Boolean(passwordErrorMsg));
      setPasswordErrorMessage(passwordErrorMsg);
      setConfirmPasswordError(Boolean(confirmPasswordErrorMsg));
      setConfirmPasswordErrorMessage(confirmPasswordErrorMsg);
      setAgreementError(!isAgreed);
      return;
    }

    setIinError(false);
    setIinErrorMessage('');
    setEmailError(false);
    setEmailErrorMessage('');
    setPasswordError(false);
    setPasswordErrorMessage('');
    setConfirmPasswordError(false);
    setConfirmPasswordErrorMessage('');

    setLoading(true);

    const payload = {
      username: !iin ? iinForBin : iin,
      iin: isSmsContext ? iin : iinForBin,
      enabled: true,
      physPersonFullname,
      bin: bin, 
      orgName,
      email: email,
      firstName: isSmsContext ? name : firstName,
      lastName: isSmsContext ? surname : lastName,
      credentials: [
        {
          type: 'password',
          value: password,
          temporary: false
        }
      ]
    };

    try {
      const response = await axios.post('/kcssobackext/sso/register', payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      let iinForResetCode = !iin ? iinForBin : iin;

      await sendResetCode(iinForResetCode);

    } catch (error) {
      if (error.response && error.response.data && typeof error.response.data === 'string') {
        const errorText = error.response.data;
        if (errorText.includes('java.lang.IllegalStateException: RESTEASY003765: Response is closed')) {
          setErrorMessage(t('register.userAlreadyRegistered'));
        } else if (errorText.includes('jakarta.ws.rs.BadRequestException: Failed to create user. Check your data.')) {
          setErrorMessage(t('register.emailAlreadyRegistered'));
        } else {
          setErrorMessage(t('register.registrationError'));
        }
      } else {
        setErrorMessage(t('register.registrationError'));
      }
      console.error('Registration failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = async (event) => {
    event.preventDefault();
    if (!isCodeComplete(verificationCode)) {
      setErrorMessage(t('error.invalidCodeFormat'));
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post('/kcssobackext/sso/external/checkVerificationCode', {
        iinOrBin,
        code: verificationCode,
        method: 'email'
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      if (response.data === true) {
        setShowLogin(true);
        setErrorMessage('');
      } else {
        setErrorMessage(t('register.invalidVerificationCode'));
      }
    } catch (error) {
      if (error.response) {
        const codeErrorMessage = getCodeErrorMessage(error.response.status, error.response.data, t);
        if (codeErrorMessage) {
          setErrorMessage(codeErrorMessage);
        } else {
          console.error('Code verification failed:', error);
          setErrorMessage(t('register.codeVerificationError'));
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLoginClick = async (password) => {
    setErrorMessage('');
    setLoading(true);

    const key = CryptoJS.enc.Hex.parse('10c3417e0b38987f3b44528d999a090efd708bcef15d6357c252bcd42b4cf03d');
    const iv = CryptoJS.enc.Hex.parse('315f48391c3d3cc857128287f8457d47');
    const encryptedUsername = CryptoJS.AES.encrypt(iinOrBin, key, { iv: iv }).toString();
    const encryptedPassword = CryptoJS.AES.encrypt(password, key, { iv: iv }).toString();

    const clientId = sessionStorage.getItem('client-id');

    try {
      const response = await axios.post('/kcssobackext/sso/external/login', {
        username: encryptedUsername,
        password: encryptedPassword,
        clientId: Number(clientId)
      });

      const { access_token, refresh_token } = response.data;
      sessionStorage.setItem('access_token', access_token);
      sessionStorage.setItem('refresh_token', refresh_token);

      await getUserInfo(iinOrBin, access_token);

      redirectToReferrer(response.data);

    } catch (error) {
      if (error.response) {
        const errorText = await error.response.data;
        if (errorText.includes('401 Unauthorized')) {
          setErrorMessage(t('register.invalidPassword'));
        } else {
          setErrorMessage(t('register.serverError'));
        }
      } else {
        setErrorMessage(t('register.serverError'));
      }
    }
    setLoading(false);
  };

  const getUserInfo = async (iin, token) => {
    try {
      const response = await axios.get(`/kcssobackext/sso/getInfo?iinOrBin=${iin}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (response.data) {
        sessionStorage.setItem('email', response.data.email);
      }
    } catch (error) {
      console.error('Failed to get user info:', error);
      setErrorMessage(t('register.getUserInfoError'));
    }
  };

  const fetchRedirectUrl = async (clientId) => {
    try {
      const response = await axios.get(`/kcssobackext/client/${clientId}`);
      if (response.data && response.data.redirectUrl) {
        return response.data.redirectUrl;
      } else {
        throw new Error("Redirect URL not found in response");
      }
    } catch (error) {
      console.error("Error fetching redirect URL:", error);
      throw error;
    }
  };

  const sendPostMessage = (secretCode, backurl) => {
    if (window.opener) {
      window.opener.postMessage(
        { 
          type: 'AUTH_SECRET_CODE', 
          secretCode: secretCode,
          backurl: backurl
        },
        "*"
      );
      window.close();
    }
  };

  const redirectToReferrer = async (secretCode) => {
    const clientId = sessionStorage.getItem('client-id');
    const backurl = sessionStorage.getItem('backurl');
    
    if (!clientId) {
      console.error("Client ID is missing");
      return;
    }
  
    try {
      // const validationResponse = await axios.post('/kcssobackext/sso/external/validateSecretCode', {
      //   secretCode: secretCode
      // });
      
      // if (validationResponse.data !== true) {
      //   Cookies.remove('secretCode');
      //   return;
      // }

      if (window.opener) {
        sendPostMessage(secretCode, backurl);
        return;
      }

      const redirectUrl = await fetchRedirectUrl(clientId);
  
      const url = new URL(redirectUrl);
      url.searchParams.set('client-id', clientId);
      url.searchParams.set('secret-code', secretCode);
    } catch (error) {
      console.error("Error during redirection:", error);
      navigate('/');
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleCorrectEmailClick = (e) => {
    e.preventDefault();
    setCodeSent(false);
    setShowEmailConfirmation(true);
  };

  const handleEmailSubmit = async () => {
    if (!email) return;

    setLoading(true);
    try {
      const response = await axios.post('/kcssobackext/sso/external/changeEmail', {
        iinOrBin: iin,
        email: email
      });

      if (response.status === 200) {
        setShowEmailConfirmation(false);
        setCodeSent(true);
      } else {
        alert(t('register.emailConfirmationError'));
      }
    } catch (error) {
      console.error(t('register.emailConfirmationError'), error);
      alert(t('register.emailConfirmationError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <h2>{showLogin ? t('register.loginTitle') : t('register.registerTitle')}</h2>
      <Paper className={styles.paper}>
        {!showLogin && !codeSent && !showEmailConfirmation ? (
          <form onSubmit={handleSubmit} onKeyDown={handleRegisterKeyDown}>
            <Typography className={styles.title}>{t('register.iin')}</Typography>
            <TextField
              fullWidth
              variant="outlined"
              margin="normal"
              placeholder={t('register.iinPlaceholder')}
              value={!iin ? iinForBin : iin}
              onChange={(e) => setIin(e.target.value)}
              error={iinError}
              helperText={iinErrorMessage}
              className={iinError ? styles.error : ''}
              disabled={true}
            />

            {isSmsContext && (
              <>
                <Typography className={styles.title}>Фамилия</Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  margin="normal"
                  value={surname}
                  onChange={(e) => setSurname(e.target.value.trim())}
                  placeholder="Введите фамилию"
                  error={surnameError}
                  helperText={surnameErrorMessage}
                  className={surnameError ? styles.error : ''}
                  required
                />
                <Typography className={styles.title}>Имя</Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  margin="normal"
                  value={name}
                  onChange={(e) => setName(e.target.value.trim())}
                  placeholder="Введите имя"
                  error={nameError}
                  helperText={nameErrorMessage}
                  className={nameError ? styles.error : ''}
                  required
                />
                <Typography className={styles.title}>Отчество</Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  margin="normal"
                  value={patronymic}
                  onChange={(e) => setPatronymic(e.target.value.trim())}
                  placeholder="Введите отчество (если имеется)"
                />
              </>
            )}

            <Typography className={styles.title}>{t('register.email')}</Typography>
            <TextField
              fullWidth
              variant="outlined"
              margin="normal"
              placeholder={t('register.emailPlaceholder')}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={handleEmailKeyDown}
              error={emailError || errorMessage.includes(t('register.userAlreadyRegistered')) || errorMessage.includes(t('register.emailAlreadyRegistered'))}
              helperText={
                emailErrorMessage ||
                (errorMessage.includes(t('register.userAlreadyRegistered'))
                  ? t('register.userAlreadyRegistered')
                  : errorMessage.includes(t('register.emailAlreadyRegistered'))
                  ? t('register.emailAlreadyRegistered')
                  : '')
              }
              className={(emailError || errorMessage.includes(t('register.userAlreadyRegistered')) || errorMessage.includes(t('register.emailAlreadyRegistered'))) ? styles.error : ''}
            />
            <Typography className={styles.title}>{t('register.password')}</Typography>
            <TextField
              fullWidth
              variant="outlined"
              margin="normal"
              placeholder={t('register.passwordPlaceholder')}
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={passwordError}
              helperText={passwordErrorMessage}
              className={passwordError ? styles.error : ''}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Typography className={styles.title}>{t('register.confirmPassword')}</Typography>
            <TextField
              fullWidth
              variant="outlined"
              margin="normal"
              placeholder={t('register.confirmPasswordPlaceholder')}
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              error={confirmPasswordError}
              helperText={confirmPasswordErrorMessage}
              className={confirmPasswordError ? styles.error : ''}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle confirm password visibility"
                      onClick={handleClickShowConfirmPassword}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={isAgreed}
                  onChange={(e) => setIsAgreed(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <span
                  style={{ textDecoration: 'underline', cursor: 'pointer', fontSize: '12px' }}
                  onClick={handleOpenModal}
                >
                  {t('register.privacyPolicy')}
                </span>
              }
              className={agreementError ? styles.error : ''}
            />
            {agreementError && (
              <Typography color="error" variant="body2">
                Необходимо согласиться с политикой конфиденциальности
              </Typography>
            )}
            <Button
              fullWidth
              type="submit"
              variant="contained"
              color="primary"
              className={styles.submitButton}
              disabled={!isAgreed || loading}
            >
              {loading ? <CircularProgress size={24} /> : t('register.submit')}
            </Button>
          </form>
        ) : codeSent && !showLogin ? (
          <form onSubmit={handleCodeSubmit} onKeyDown={handleCodeKeyDown}>
            <Typography className={styles.title}>
              {t('register.emailConfirmationTitle')}
            </Typography>
            <Typography className={styles.description}>
            { email }
            </Typography>
            <Typography className={styles.description}>
              {t('register.emailConfirmationDescription')}
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              margin="normal"
              placeholder={t('register.codePlaceholder')}
              value={verificationCode}
              onChange={(e) => setVerificationCode(sanitizeCode(e.target.value))}
              inputProps={{ maxLength: CODE_LENGTH, inputMode: 'numeric', pattern: '[0-9]*' }}
              error={Boolean(errorMessage)}
              helperText={errorMessage}
              className={Boolean(errorMessage) ? styles.error : ''}
            />
            <Typography className={styles.errorMessage}>
              <span>{t('register.wrongEmail')} </span>
              <a href='#'
                onClick={handleCorrectEmailClick}
                style={{ textDecoration: 'underline', color: '#007bff' }}
              >
                {t('register.correctEmail')}
              </a>
            </Typography>
            <Typography>{resendTimer > 0 ? `${t('sms.resendCode')} (${resendTimer}s)` : ''}</Typography>
            <div>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                className={styles.submitButton}
                onClick={() => sendResetCode(!iin ? iinForBin : iin)}
                disabled={isResendDisabled}
              >
                {t('sms.resendCode')}
              </Button>
              <Button
                fullWidth
                type="submit"
                variant="contained"
                color="primary"
                className={styles.submitButton}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : t('register.submitCode')}
              </Button>
            </div>
          </form>
        ) : showEmailConfirmation ? (
          <div className={styles.emailConfirmationContainer}>
            <h2>{t('register.emailConfirmationTitle')}</h2>
            <p>{t('register.enterEmail')}</p>
            <div className={styles.emailConfirmationContent}>
              <TextField
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={handleEmailKeyDown}
                variant="outlined"
                margin="normal"
                placeholder={t('register.innerEmail')}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleEmailSubmit}
              >
                {t('register.sendCode')}
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <p className={styles.loginLabel}>{t('sms.enterPassword')}</p>
            <PasswordForm
              onSubmit={handleLoginClick}
              isLoading={loading}
              errorMessage={errorMessage}
              passwordLabel={t('login.password')}
              submitButtonText={t('login.loginButton')}
            />
          </div>
        )}
      </Paper>
      <ReactModal
        isOpen={isModalOpen}
        onRequestClose={handleCloseModal}
        shouldCloseOnOverlayClick={false}
        shouldCloseOnEsc={false}
        style={{
          overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1300,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          },
          content: {
            position: 'relative',
            inset: 'auto',
            border: 'none',
            borderRadius: '4px',
            padding: '24px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto',
            margin: '0 auto'
          }
        }}
      >
        <Typography variant="h6" component="h2" gutterBottom>
          {t('register.privacyPolicyTitle')}
        </Typography>
        <Typography variant="body1" paragraph>
          {t('register.privacyPolicyText1')}
        </Typography>
        <Typography variant="body1" paragraph>
          {t('register.privacyPolicyText2')}
        </Typography>
        <Typography variant="body1" paragraph>
          {t('register.privacyPolicyText3')}
        </Typography>
        <Typography variant="body1" paragraph>
          {t('register.privacyPolicyText4')}
        </Typography>
        <Typography variant="body1" paragraph>
          {t('register.privacyPolicyText5')}
        </Typography>
        <Button 
          onClick={handleCloseModal} 
          color="primary"
          variant="contained"
          style={{ marginTop: '16px' }}
        >
          {t('register.privacyPolicyClose')}
        </Button>
      </ReactModal>
    </div>
  );
};